import { create } from 'zustand';
import { persist } from 'zustand/middleware';

const today = () => new Date().toISOString().slice(0, 10);

const useProgressStore = create(
  persist(
    (set, get) => ({
      // Study streak
      streak: 0,
      lastStudyDate: null,

      // Daily goal (questions per day)
      dailyGoal: 20,

      // Activity log: { id, type, description, timestamp, meta }
      activities: [],

      // Exam results: { id, type, score, total, timeTaken, date, categoryBreakdown }
      examResults: [],

      // Daily counts: { 'YYYY-MM-DD': { questionsAnswered, correctAnswers } }
      dailyCounts: {},

      // ── Streak management ──
      updateStreak: () => {
        const { lastStudyDate, streak } = get();
        const todayStr = today();
        if (lastStudyDate === todayStr) return; // already updated today

        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);
        const yesterdayStr = yesterday.toISOString().slice(0, 10);

        const newStreak = lastStudyDate === yesterdayStr ? streak + 1 : 1;
        set({ streak: newStreak, lastStudyDate: todayStr });
      },

      // ── Daily tracking ──
      recordAnswer: (isCorrect) => {
        const todayStr = today();
        set((state) => {
          const counts = { ...state.dailyCounts };
          if (!counts[todayStr]) counts[todayStr] = { questionsAnswered: 0, correctAnswers: 0 };
          counts[todayStr].questionsAnswered += 1;
          if (isCorrect) counts[todayStr].correctAnswers += 1;
          return { dailyCounts: counts };
        });
        get().updateStreak();
      },

      getTodayProgress: () => {
        const counts = get().dailyCounts[today()] || { questionsAnswered: 0, correctAnswers: 0 };
        return counts;
      },

      // ── Activity feed ──
      addActivity: (type, description, meta = {}) => {
        const activity = {
          id: Date.now() + Math.random(),
          type, // 'practice', 'exam', 'flashcard', 'achievement'
          description,
          timestamp: Date.now(),
          meta,
        };
        set((state) => ({
          activities: [activity, ...state.activities].slice(0, 100), // keep last 100
        }));
      },

      getRecentActivities: (n = 10) => {
        return get().activities.slice(0, n);
      },

      // ── Exam results ──
      saveExamResult: (result) => {
        const examResult = {
          id: Date.now(),
          date: new Date().toISOString(),
          ...result,
        };
        set((state) => ({
          examResults: [examResult, ...state.examResults].slice(0, 50),
        }));
        get().addActivity(
          'exam',
          `Completed ${result.examType}: ${result.score}/${result.total} (${Math.round((result.score / result.total) * 100)}%)`,
          { examId: examResult.id }
        );
        // Record each answer (prefer actual booleans when available)
        if (Array.isArray(result.answers) && result.answers.length) {
          result.answers.forEach((isCorrect) => get().recordAnswer(!!isCorrect));
        } else {
          for (let i = 0; i < result.total; i++) {
            get().recordAnswer(i < result.score);
          }
        }
        return examResult.id;
      },

      savePracticeResult: (result) => {
        const { score, total, categoryBreakdown, timeTaken } = result;
        get().addActivity(
          'practice',
          `Practice session: ${score}/${total} correct (${Math.round((score / total) * 100)}%)`,
          result
        );
        if (Array.isArray(result.answers) && result.answers.length) {
          result.answers.forEach((a) => get().recordAnswer(!!a.isCorrect));
        } else {
          for (let i = 0; i < total; i++) {
            get().recordAnswer(i < score);
          }
        }
      },

      // ── Exam date ──
      examDate: null,
      setExamDate: (dateStr) => set({ examDate: dateStr }),

      getDaysUntilExam: () => {
        const { examDate } = get();
        if (!examDate) return null;
        const exam = new Date(examDate);
        const now = new Date();
        now.setHours(0, 0, 0, 0);
        exam.setHours(0, 0, 0, 0);
        const diff = Math.ceil((exam - now) / (1000 * 60 * 60 * 24));
        return diff;
      },

      getDailyTargetToFinish: (totalQuestions, answeredCount) => {
        const days = get().getDaysUntilExam();
        if (!days || days <= 0) return null;
        const remaining = totalQuestions - answeredCount;
        if (remaining <= 0) return 0;
        return Math.ceil(remaining / days);
      },

      // ── Setters ──
      setDailyGoal: (goal) => set({ dailyGoal: goal }),

      // ── Reset ──
      resetAll: () =>
        set({
          streak: 0,
          lastStudyDate: null,
          activities: [],
          examResults: [],
          dailyCounts: {},
        }),
    }),
    {
      name: 'endoboard-progress',
    }
  )
);

export default useProgressStore;
